import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, doc, updateDoc, orderBy, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import LoadingState from '../shared/LoadingState';
import { 
  DollarSign, 
  User, 
  CreditCard, 
  CheckCircle, 
  AlertCircle,
  Clock,
  X,
  Plus,
  FileText,
  Receipt
} from 'lucide-react';
import './Payments.css';
import BusinessFinanceLedger from './BusinessFinanceLedger';

const getDateValue = (date) => {
  if (!date) return null;
  const parsed = date.toDate ? date.toDate() : new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildCashSummaries = (sessions, sales) => {
  return sessions.map(session => {
    const sessionSales = sales.filter(sale => sale.cashSessionId === session.id);
    const paidTotal = sessionSales.reduce((sum, sale) => sum + (Number(sale.totalAmount) || 0), 0);
    const openedAt = session.openedAt || session.createdAt;

    return {
      ...session,
      date: openedAt,
      paidTotal,
      salesCount: sessionSales.length,
      openingAmount: Number(session.openingAmount) || 0,
      closingAmount: session.closingAmount === undefined || session.closingAmount === null ? null : Number(session.closingAmount) || 0,
      operatorOpen: session.openedByEmail || '-',
      operatorClose: session.closedByEmail || '-'
    };
  }).sort((a, b) => {
    const aTime = getDateValue(a.date)?.getTime() || 0;
    const bTime = getDateValue(b.date)?.getTime() || 0;
    return bTime - aTime;
  });
};

const Payments = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const companyId = userData?.role === 'collaborator'
    ? userData?.currentCompanyId
    : userData?.companyId;

  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [posCashSummaries, setPosCashSummaries] = useState([]);
  const [filteredPosCashSummaries, setFilteredPosCashSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Payment form
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: 'cash',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Filtros
  const [filters, setFilters] = useState({
    status: 'all',
    paymentStatus: 'all',
    startDate: '',
    endDate: '',
    search: '',
    collaboratorEmail: userData?.role === 'collaborator' ? userData.email : 'all'
  });

  const [collaborators, setCollaborators] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    partial: 0,
    posPaid: 0,
    posCashSessions: 0
  });

  const checkedInPendingPayments = reservations.filter(reservation => (
    reservation.checkInStatus === 'checked_in' &&
    reservation.paymentStatus !== 'paid' &&
    (Number(reservation.totalAmount) || 0) > (Number(reservation.paidAmount) || 0)
  ));

  useEffect(() => {
    if (!companyId) return undefined;

    setLoading(true);
    const pendingInitialLoads = new Set(['reservations', 'sales', 'sessions']);
    let sales = [];
    let sessions = [];

    const markAsLoaded = (source) => {
      pendingInitialLoads.delete(source);
      if (pendingInitialLoads.size === 0) setLoading(false);
    };

    const updateCashSummaries = () => {
      setPosCashSummaries(buildCashSummaries(sessions, sales));
    };

    const handleListenerError = (source, label) => (error) => {
      console.error(`Erro ao acompanhar ${label}:`, error);
      markAsLoaded(source);
    };

    const reservationsQuery = query(
      collection(db, `companies/${companyId}/reservations`),
      orderBy('date', 'desc')
    );
    const unsubscribeReservations = onSnapshot(
      reservationsQuery,
      snapshot => {
        let data = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));

        if (userData?.role === 'collaborator') {
          data = data.filter(reservation => reservation.createdByEmail === userData.email);
        }

        setReservations(data.filter(reservation =>
          reservation.status === 'confirmed' || reservation.status === 'completed'
        ));
        markAsLoaded('reservations');
      },
      handleListenerError('reservations', 'as reservas')
    );

    const unsubscribeSales = onSnapshot(
      query(
        collection(db, `companies/${companyId}/posSales`),
        where('status', '==', 'paid')
      ),
      snapshot => {
        sales = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
        updateCashSummaries();
        markAsLoaded('sales');
      },
      handleListenerError('sales', 'as vendas do PDV')
    );

    const unsubscribeSessions = onSnapshot(
      collection(db, `companies/${companyId}/cashSessions`),
      snapshot => {
        sessions = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
        updateCashSummaries();
        markAsLoaded('sessions');
      },
      handleListenerError('sessions', 'os caixas do PDV')
    );

    let unsubscribeCollaborators = () => {};
    if (userData?.role !== 'collaborator') {
      unsubscribeCollaborators = onSnapshot(
        collection(db, `companies/${companyId}/collaborators`),
        snapshot => setCollaborators(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))),
        error => console.error('Erro ao acompanhar colaboradores:', error)
      );
    }

    return () => {
      unsubscribeReservations();
      unsubscribeSales();
      unsubscribeSessions();
      unsubscribeCollaborators();
    };
  }, [companyId, userData?.email, userData?.role]);

  useEffect(() => {
    applyFilters();
  }, [filters, reservations, posCashSummaries]);

  const calculateSummary = (data, cashData = filteredPosCashSummaries) => {
    const total = data.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    const paid = data.reduce((sum, r) => sum + (r.paidAmount || 0), 0);
    const pending = total - paid;
    const posPaid = cashData.reduce((sum, cash) => sum + (cash.paidTotal || 0), 0);
    
    const partialCount = data.filter(r => r.paymentStatus === 'partial').length;

    setSummary({
      total,
      paid,
      pending,
      partial: partialCount,
      posPaid,
      posCashSessions: cashData.length
    });
  };

  const isInsideDateFilter = (date) => {
    const value = getDateValue(date);
    if (!value) return false;

    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      if (value < startDate) return false;
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      if (value > endDate) return false;
    }

    return true;
  };

  const applyFilters = () => {
    let filtered = [...reservations];

    // Filtro de status de reserva
    if (filters.status !== 'all') {
      filtered = filtered.filter(r => r.status === filters.status);
    }

    // Filtro de status de pagamento
    if (filters.paymentStatus !== 'all') {
      filtered = filtered.filter(r => r.paymentStatus === filters.paymentStatus);
    }

    // Filtro de data
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(r => {
        const rDate = r.date.toDate ? r.date.toDate() : new Date(r.date);
        return rDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(r => {
        const rDate = r.date.toDate ? r.date.toDate() : new Date(r.date);
        return rDate <= endDate;
      });
    }

    // Filtro de colaborador
    if (filters.collaboratorEmail !== 'all') {
      filtered = filtered.filter(r => r.createdByEmail === filters.collaboratorEmail);
    }

    // Filtro de busca
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(r => 
        r.clientName?.toLowerCase().includes(search) ||
        r.serviceName?.toLowerCase().includes(search) ||
        r.id?.toLowerCase().includes(search)
      );
    }

    setFilteredReservations(filtered);
    const filteredCash = posCashSummaries.filter(cash => {
      if ((filters.startDate || filters.endDate) && !isInsideDateFilter(cash.date)) return false;
      if (filters.collaboratorEmail !== 'all') {
        const isOperator = cash.operatorOpen === filters.collaboratorEmail || cash.operatorClose === filters.collaboratorEmail;
        if (!isOperator) return false;
      }
      if (filters.search) {
        const search = filters.search.toLowerCase();
        return cash.establishmentName?.toLowerCase().includes(search) ||
          cash.operatorOpen?.toLowerCase().includes(search) ||
          cash.operatorClose?.toLowerCase().includes(search) ||
          cash.id?.toLowerCase().includes(search);
      }
      return true;
    });
    setFilteredPosCashSummaries(filteredCash);
    calculateSummary(filtered, filteredCash);
  };

  const handleOpenPaymentModal = (reservation) => {
    setSelectedReservation(reservation);
    const remaining = (reservation.totalAmount || 0) - (reservation.paidAmount || 0);
    setPaymentData({
      amount: remaining.toFixed(2),
      method: 'cash',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowPaymentModal(true);
  };

  useEffect(() => {
    const reservationId = searchParams.get('reservation');
    if (!reservationId || reservations.length === 0) return;
    const reservation = reservations.find(item => item.id === reservationId);
    if (reservation && reservation.paymentStatus !== 'paid') {
      handleOpenPaymentModal(reservation);
    }
    setSearchParams({}, { replace: true });
    // A URL deve ser consumida somente uma vez após o listener carregar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, searchParams, setSearchParams]);

  const handleRegisterPayment = async () => {
    if (!selectedReservation) return;

    const amount = parseFloat(paymentData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Valor inválido');
      return;
    }

    const currentPaid = selectedReservation.paidAmount || 0;
    const newPaidAmount = currentPaid + amount;
    const totalAmount = selectedReservation.totalAmount || 0;

    if (newPaidAmount > totalAmount) {
      alert('O valor pago não pode exceder o valor total da reserva');
      return;
    }

    try {
      const payment = {
        amount,
        method: paymentData.method,
        date: new Date(paymentData.date),
        notes: paymentData.notes,
        registeredBy: userData?.email || 'unknown',
        registeredAt: new Date()
      };

      const payments = selectedReservation.payments || [];
      payments.push(payment);

      let newPaymentStatus;
      if (newPaidAmount >= totalAmount) {
        newPaymentStatus = 'paid';
      } else if (newPaidAmount > 0) {
        newPaymentStatus = 'partial';
      } else {
        newPaymentStatus = 'unpaid';
      }

      const reservationRef = doc(db, `companies/${companyId}/reservations`, selectedReservation.id);
      await updateDoc(reservationRef, {
        paidAmount: newPaidAmount,
        paymentStatus: newPaymentStatus,
        payments: payments,
        lastPaymentDate: new Date()
      });

      alert('Pagamento registrado com sucesso!');
      setShowPaymentModal(false);
      setSelectedReservation(null);
      setPaymentData({ amount: '', method: 'cash', date: new Date().toISOString().split('T')[0], notes: '' });
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      alert('Erro ao registrar pagamento');
    }
  };

  const handleShowHistory = (reservation) => {
    setSelectedReservation(reservation);
    setShowHistoryModal(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString('pt-BR');
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={20} color="#16a34a" />;
      case 'partial':
        return <Clock size={20} color="#f59e0b" />;
      case 'unpaid':
        return <AlertCircle size={20} color="#dc2626" />;
      default:
        return <AlertCircle size={20} color="#6b7280" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Dinheiro',
      credit_card: 'Cartão de Crédito',
      debit_card: 'Cartão de Débito',
      pix: 'PIX',
      bank_transfer: 'Transferência',
      check: 'Cheque',
      other: 'Outro'
    };
    return methods[method] || method;
  };

  return (
    <div className="payments-container">
      <div className="payments-header">
        <div>
          <h1>Pagamentos</h1>
          <p>Gestão de recebimentos e controle financeiro</p>
        </div>
      </div>

      {checkedInPendingPayments.length > 0 && (
        <section className="checked-payment-alert" aria-labelledby="checked-payment-alert-title">
          <div className="checked-payment-alert-header">
            <AlertCircle size={22} />
            <div>
              <h2 id="checked-payment-alert-title">Check-in realizado com pagamento pendente</h2>
              <p>{checkedInPendingPayments.length} reserva(s) precisam de lançamento de pagamento.</p>
            </div>
          </div>
          <div className="checked-payment-alert-list">
            {checkedInPendingPayments.map(reservation => {
              const pending = (Number(reservation.totalAmount) || 0) - (Number(reservation.paidAmount) || 0);
              return (
                <div className="checked-payment-alert-item" key={reservation.id}>
                  <div>
                    <strong>{reservation.clientName || 'Cliente'}</strong>
                    <span>{reservation.serviceName || 'Serviço'} · {formatDate(reservation.date)}</span>
                  </div>
                  <strong>{formatCurrency(pending)}</strong>
                  <button type="button" onClick={() => handleOpenPaymentModal(reservation)}>
                    <Plus size={16} />
                    Lançar pagamento
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <BusinessFinanceLedger companyId={companyId} userData={userData} />

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#6366f1' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total em Reservas</p>
            <h2 className="summary-value">{formatCurrency(summary.total)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
            <CheckCircle size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total Recebido</p>
            <h2 className="summary-value">{formatCurrency(summary.paid)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
            <AlertCircle size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Valor Pendente</p>
            <h2 className="summary-value">{formatCurrency(summary.pending)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <Clock size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Pagamentos Parciais</p>
            <h2 className="summary-value">{summary.partial}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#ecfeff', color: '#0891b2' }}>
            <Receipt size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Pago no PDV</p>
            <h2 className="summary-value">{formatCurrency(summary.posPaid)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#f0fdf4', color: '#15803d' }}>
            <CreditCard size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Caixas do PDV</p>
            <h2 className="summary-value">{summary.posCashSessions}</h2>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="payments-filters">
        <div className="filter-group">
          <label>Buscar</label>
          <input
            type="text"
            placeholder="Cliente, serviço, caixa ou operador..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Status Reserva</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="all">Todos</option>
            <option value="confirmed">Confirmada</option>
            <option value="completed">Concluída</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status Pagamento</label>
          <select
            value={filters.paymentStatus}
            onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
          >
            <option value="all">Todos</option>
            <option value="unpaid">Não Pago</option>
            <option value="partial">Parcial</option>
            <option value="paid">Pago</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Data Inicial</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Data Final</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>

        {userData?.role !== 'collaborator' && (
          <div className="filter-group">
            <label>Colaborador</label>
            <select
              value={filters.collaboratorEmail}
              onChange={(e) => setFilters({ ...filters, collaboratorEmail: e.target.value })}
            >
              <option value="all">Todos</option>
              {collaborators.map(collab => (
                <option key={collab.id} value={collab.userEmail}>
                  {collab.userName}
                </option>
              ))}
            </select>
          </div>
        )}

        <button 
          className="btn-clear-filters" 
          onClick={() => setFilters({
            status: 'all',
            paymentStatus: 'all',
            startDate: '',
            endDate: '',
            search: '',
            collaboratorEmail: userData?.role === 'collaborator' ? userData.email : 'all'
          })}
        >
          <X size={18} />
          Limpar Filtros
        </button>
      </div>

      {/* Payments Table */}
      {loading ? (
        <LoadingState
          variant="block"
          title="Carregando pagamentos"
          subtitle="Atualizando valores e status"
          bars={2}
        />
      ) : filteredReservations.length === 0 ? (
        <div className="payments-empty">
          <DollarSign size={64} color="#ccc" />
          <p>Nenhum pagamento encontrado</p>
        </div>
      ) : (
        <div className="payments-table-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Cliente</th>
                <th>Serviço</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Pendente</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredReservations.map((reservation) => {
                const pending = (reservation.totalAmount || 0) - (reservation.paidAmount || 0);
                return (
                  <tr key={reservation.id}>
                    <td data-label="Data">{formatDate(reservation.date)}</td>
                    <td data-label="Cliente">
                      <div className="client-info">
                        <User size={16} />
                        {reservation.clientName}
                      </div>
                    </td>
                    <td data-label="Serviço">{reservation.serviceName}</td>
                    <td data-label="Total" className="amount-cell">{formatCurrency(reservation.totalAmount)}</td>
                    <td data-label="Pago" className="amount-cell paid">{formatCurrency(reservation.paidAmount || 0)}</td>
                    <td data-label="Pendente" className="amount-cell pending">{formatCurrency(pending)}</td>
                    <td data-label="Status">
                      <div className="payment-status">
                        {getPaymentStatusIcon(reservation.paymentStatus)}
                        <span className={`payment-badge payment-${reservation.paymentStatus}`}>
                          {reservation.paymentStatus === 'paid' ? 'Pago' : 
                           reservation.paymentStatus === 'partial' ? 'Parcial' : 'Pendente'}
                        </span>
                      </div>
                    </td>
                    <td data-label="Ações">
                      <div className="action-buttons">
                        {pending > 0 && (
                          <button
                            className="btn-action btn-payment"
                            onClick={() => handleOpenPaymentModal(reservation)}
                            title="Registrar Pagamento"
                          >
                            <Plus size={16} />
                            Registrar
                          </button>
                        )}
                        {reservation.payments && reservation.payments.length > 0 && (
                          <button
                            className="btn-action btn-history"
                            onClick={() => handleShowHistory(reservation)}
                            title="Ver Histórico"
                          >
                            <FileText size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div className="payments-section">
          <div className="payments-section-header">
            <div>
              <h2>Receitas do PDV</h2>
              <p>Caixas com vendas pagas, operador, abertura e fechamento.</p>
            </div>
          </div>

          {filteredPosCashSummaries.length === 0 ? (
            <div className="payments-empty payments-empty-compact">
              <Receipt size={48} color="#ccc" />
              <p>Nenhum caixa do PDV encontrado no período</p>
            </div>
          ) : (
            <div className="payments-table-container">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th>Caixa</th>
                    <th>Operador</th>
                    <th>Abertura</th>
                    <th>Fechamento</th>
                    <th>Vendas pagas</th>
                    <th>Total pago</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosCashSummaries.map(cash => (
                    <tr key={cash.id}>
                      <td data-label="Dia">{formatDate(cash.date)}</td>
                      <td data-label="Caixa">{cash.establishmentName || 'PDV'}</td>
                      <td data-label="Operador">
                        <div className="cash-operator">
                          <span>Abriu: {cash.operatorOpen}</span>
                          <span>Fechou: {cash.operatorClose}</span>
                        </div>
                      </td>
                      <td data-label="Abertura" className="amount-cell">{formatCurrency(cash.openingAmount)}</td>
                      <td data-label="Fechamento" className="amount-cell">{cash.closingAmount === null ? '-' : formatCurrency(cash.closingAmount)}</td>
                      <td data-label="Vendas pagas">{cash.salesCount}</td>
                      <td data-label="Total pago" className="amount-cell paid">{formatCurrency(cash.paidTotal)}</td>
                      <td data-label="Status">
                        <span className={`payment-badge payment-${cash.status === 'closed' ? 'paid' : 'partial'}`}>
                          {cash.status === 'closed' ? 'Fechado' : 'Aberto'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedReservation && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registrar Pagamento</h2>
              <button className="btn-close" onClick={() => setShowPaymentModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="reservation-info">
                <p><strong>Cliente:</strong> {selectedReservation.clientName}</p>
                <p><strong>Serviço:</strong> {selectedReservation.serviceName}</p>
                <p><strong>Total:</strong> {formatCurrency(selectedReservation.totalAmount)}</p>
                <p><strong>Já Pago:</strong> {formatCurrency(selectedReservation.paidAmount || 0)}</p>
                <p><strong>Pendente:</strong> {formatCurrency((selectedReservation.totalAmount || 0) - (selectedReservation.paidAmount || 0))}</p>
              </div>

              <div className="form-group">
                <label>Valor do Pagamento*</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  placeholder="0,00"
                />
              </div>

              <div className="form-group">
                <label>Método de Pagamento*</label>
                <select
                  value={paymentData.method}
                  onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                >
                  <option value="cash">Dinheiro</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                  <option value="pix">PIX</option>
                  <option value="bank_transfer">Transferência Bancária</option>
                  <option value="check">Cheque</option>
                  <option value="other">Outro</option>
                </select>
              </div>

              <div className="form-group">
                <label>Data do Pagamento*</label>
                <input
                  type="date"
                  value={paymentData.date}
                  onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  placeholder="Informações adicionais sobre o pagamento..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPaymentModal(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleRegisterPayment}>
                <CheckCircle size={18} />
                Confirmar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedReservation && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Histórico de Pagamentos</h2>
              <button className="btn-close" onClick={() => setShowHistoryModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="reservation-info">
                <p><strong>Cliente:</strong> {selectedReservation.clientName}</p>
                <p><strong>Serviço:</strong> {selectedReservation.serviceName}</p>
                <p><strong>Total:</strong> {formatCurrency(selectedReservation.totalAmount)}</p>
              </div>

              <div className="payments-history">
                <h3>Pagamentos Registrados</h3>
                {selectedReservation.payments && selectedReservation.payments.length > 0 ? (
                  <div className="payments-list">
                    {selectedReservation.payments.map((payment, index) => (
                      <div key={index} className="payment-item">
                        <div className="payment-item-header">
                          <div className="payment-amount">
                            <CreditCard size={18} />
                            <strong>{formatCurrency(payment.amount)}</strong>
                          </div>
                          <span className="payment-date">
                            {formatDate(payment.date)}
                          </span>
                        </div>
                        <div className="payment-item-details">
                          <p><strong>Método:</strong> {getPaymentMethodLabel(payment.method)}</p>
                          {payment.notes && <p><strong>Observações:</strong> {payment.notes}</p>}
                          <p className="payment-meta">
                            Registrado por {payment.registeredBy} em {formatDateTime(payment.registeredAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-payments">Nenhum pagamento registrado ainda</p>
                )}

                <div className="payment-summary">
                  <div className="summary-row">
                    <span>Total Pago:</span>
                    <strong className="paid">{formatCurrency(selectedReservation.paidAmount || 0)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>Valor Pendente:</span>
                    <strong className="pending">
                      {formatCurrency((selectedReservation.totalAmount || 0) - (selectedReservation.paidAmount || 0))}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowHistoryModal(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
